import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { apiService, Producto, SaleItem, SaleData } from '../services/api';
import panaApi, { Despacho } from '../services/panaApi';
import { printService } from '../services/printService';

interface PuntoVentaProps {
  user: any;
}

const PuntoVentaV2: React.FC<PuntoVentaProps> = ({ user }) => {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [carrito, setCarrito] = useState<SaleItem[]>(() => {
    try {
      const saved = localStorage.getItem('pos_carrito_v2');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Guardar carrito en localStorage cada vez que cambie
  useEffect(() => {
    try {
      localStorage.setItem('pos_carrito_v2', JSON.stringify(carrito));
    } catch (e) {
      console.warn('No se pudo guardar el carrito en local storage', e);
    }
  }, [carrito]);
  const [verificarStock, setVerificarStock] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('verificar_stock');
      return v !== null ? v === '1' || v === 'true' : true;
    } catch { return true; }
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [clienteNombre, setClienteNombre] = useState('');
  const [medioPago, setMedioPago] = useState(1);
  const [pagoRecibido, setPagoRecibido] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processingVenta, setProcessingVenta] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [vueltoAnterior, setVueltoAnterior] = useState(0);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [weightProduct, setWeightProduct] = useState<Producto | null>(null);
  const [weightGrams, setWeightGrams] = useState<number>(0);
  const [showQtyModal, setShowQtyModal] = useState(false);
  const [qtyProduct, setQtyProduct] = useState<Producto | null>(null);
  const [qtyValue, setQtyValue] = useState<number>(1);
  const [qtySkipStock, setQtySkipStock] = useState<boolean>(false);
  const qtyInputRef = useRef<HTMLInputElement>(null);

  const [estadoEnter, setEstadoEnter] = useState<boolean>(false);

  // Autocomplete states
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteResults, setAutocompleteResults] = useState<Producto[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    if (showQtyModal) setTimeout(() => qtyInputRef.current?.focus(), 0);
  }, [showQtyModal]);
  // Pagar voucher (Pana)
  const [showPanaModal, setShowPanaModal] = useState(false);
  const [panaCodigo, setPanaCodigo] = useState('');
  const [panaLoading, setPanaLoading] = useState(false);
  const [panaDespacho, setPanaDespacho] = useState<Despacho | null>(null);

  const buscarDespacho = async () => {
    try {
      if (!navigator.onLine) { Swal.fire({ icon:'warning', title:'Sin conexión', text:'Conéctate a Internet para consultar el despacho.' }); return; }
      if (!panaCodigo.trim()) return;
      setPanaLoading(true);
      const res = await panaApi.getDespachoByBarra(panaCodigo.trim());
      setPanaLoading(false);
      if (!res.ok || !res.despacho) {
        setPanaDespacho(null);
        Swal.fire({ icon:'info', title:'No encontrado', text: res.message || 'Despacho no encontrado' });
        return;
      }
      setPanaDespacho(res.despacho);
    } catch (e: any) {
      setPanaLoading(false);
      Swal.fire({ icon:'error', title:'Error', text: e?.message || 'Error consultando despacho' });
    }
  };

  const marcarPagado = async () => {
    if (!panaDespacho) return;
    try {
      setPanaLoading(true);
      const res = await panaApi.setEstadoPago(panaDespacho.codigo_barra, 1, user?.id_usuario);
      setPanaLoading(false);
      if (!res.ok) { Swal.fire({ icon:'error', title:'Error', text: res.message || 'No se pudo actualizar el estado' }); return; }
      Swal.fire({ icon:'success', title:'Voucher pagado', text:'El estado de pago fue actualizado.' });
      setPanaDespacho({ ...panaDespacho, estado_pago: 1 });
    } catch (e: any) {
      setPanaLoading(false);
      Swal.fire({ icon:'error', title:'Error', text: e?.message || 'Error al cambiar estado' });
    }
  };
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const productosContainerRef = useRef<HTMLDivElement | null>(null);
  const carritoContainerRef = useRef<HTMLDivElement | null>(null);
  const pagoRecibidoInputRef = useRef<HTMLInputElement | null>(null);
  const medioPagoRef = useRef<HTMLSelectElement | null>(null);
  const panaBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    cargarProductos();
    cargarFlagVerificarStock();
  }, []);

  const cargarProductos = async () => {
    try {
      if (!navigator.onLine) {
        Swal.fire({ icon: 'warning', title: 'Sin conexión', text: 'No hay conexión a Internet. Trabajando con catálogo cargado.', confirmButtonColor: '#FFC107' });
        return;
      }
      const response = await apiService.getProducts({ estado: 1 });
      if (response.estado === 1 && response.registros) {
        setProductos(response.registros);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los productos',
        confirmButtonColor: '#DC3545'
      });
    }
  };

  const cargarFlagVerificarStock = async () => {
    try {
      if (!navigator.onLine) {
        return;
      }
      const res = await apiService.getVerificarStock();
      const flag = (res?.verificar_stock ?? 1) ? true : false;
      setVerificarStock(flag);
      try { localStorage.setItem('verificar_stock', flag ? '1' : '0'); } catch {}
    } catch (e) {
      // mantener valor actual
    }
  };

  // Autocomplete logic
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length === 0) {
      setShowAutocomplete(false);
      setAutocompleteResults([]);
      return;
    }

    if (/^\d+$/.test(term)) {
      setShowAutocomplete(false);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      if (navigator.onLine) {
        try {
          const response = await apiService.getProducts({ nombre_producto: term, estado: 1 } as any);
          if (response.estado === 1 && response.registros) {
            setAutocompleteResults(response.registros.slice(0, 10));
            setShowAutocomplete(true);
            setFocusedIndex(-1);
          }
        } catch (e) {
          // fallback to local filter
          const results = productos
            .filter(p => p.nombre_producto.toLowerCase().includes(term.toLowerCase()))
            .slice(0, 10);
          setAutocompleteResults(results);
          setShowAutocomplete(true);
          setFocusedIndex(-1);
        }
      } else {
        const results = productos
          .filter(p => p.nombre_producto.toLowerCase().includes(term.toLowerCase()))
          .slice(0, 10);
        setAutocompleteResults(results);
        setShowAutocomplete(true);
        setFocusedIndex(-1);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, productos]);

  const handleKeyDownAutocomplete = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAutocomplete || autocompleteResults.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => (prev < autocompleteResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      if (focusedIndex >= 0 && focusedIndex < autocompleteResults.length) {
        e.preventDefault();
        seleccionarProductoAutocomplete(autocompleteResults[focusedIndex]);
      }
    }
  };

  const seleccionarProductoAutocomplete = (prod: Producto) => {
    const esKilo = (prod.id_unidad_medida ?? 0) === 2;
    console.log('Producto seleccionado:', prod.nombre_producto, 'id_unidad_medida:', prod.id_unidad_medida, 'esKilo:', esKilo);
    if (esKilo) {
      agregarProductoPorPeso(prod);
    } else {
      agregarAlCarrito(prod);
    }
    setSearchTerm('');
    setShowAutocomplete(false);
    searchInputRef.current?.focus();
  };

  const buscarProducto = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    try {
      const codigo = searchTerm.trim();

      // Si el autocomplete está visible y seleccionó algo con teclado, se previene este submit por el preventDefault en handleKeyDown
      // Si llega aquí y hay texto normal, no hacemos nada (el autocomplete lo maneja)
      if (!/^\d+$/.test(codigo)) {
        return;
      }

      // Manejo de códigos de peso (prefijo 25): 25 + [5 dígitos id interno] + [5 dígitos peso gramos] + checksum
      let handled = false;
      if (codigo.length >= 12 && codigo.startsWith('25')) {
        const flag = codigo.substr(6, 1);
        const idInternoStr = codigo.substr(2, 5);
        const pesoStr = codigo.substr(7, 5);
        const idInterno = parseInt(idInternoStr, 10);
        const pesoGr = parseInt(pesoStr, 10);
        const kilos = isNaN(pesoGr) ? 0 : (pesoGr / 1000);

        if (!isNaN(idInterno) && kilos > 0) {
          const byId = await apiService.getProducts({ codigo_barra: idInternoStr, estado: 1 });
          const prod = (byId.registros && Array.isArray(byId.registros)) ? byId.registros[0] : (byId.data as any);
          if (prod) {
            // Solo aplicar si unidad de medida es kilo (id_unidad_medida === 2)
            if ((prod.id_unidad_medida ?? 0) === 2) {
              setCarrito(prev => {
                /*
                const existing = prev.find(i => i.id_producto === prod.id_producto);
                if (existing) {
                  const nuevaCantidad = existing.cantidad_producto + kilos;
                  return prev.map(i => i.id_producto === prod.id_producto ? { ...i, cantidad_producto: nuevaCantidad } : i);
                }*/
                const item: SaleItem = {
                  cartId: generarCartId(),
                  id_producto: prod.id_producto,
                  cantidad_producto: kilos,
                  precio_producto: prod.precio_venta_producto, // precio por kg
                  descuento_producto: 0,
                  nombre_producto: prod.nombre_producto
                };
                return [...prev, item];
              });
              handled = true;
            }
          }
        }
      }

      if (handled) { setSearchTerm(''); return; }

      // Búsqueda por código de barras
      if (navigator.onLine) {
        const byBarcode = await apiService.getProducts({ codigo_barra_producto: codigo, estado: 1 } as any);
        const registros = (byBarcode.registros && Array.isArray(byBarcode.registros)) ? byBarcode.registros : (byBarcode.data ? [byBarcode.data as any] : []);
        if (registros.length === 1) {
          const prod = registros[0];
          const skipStock = codigo.length < 5;
          if (skipStock) {
            setQtyProduct(prod);
            setQtyValue(1);
            setQtySkipStock(true);
            setShowQtyModal(true);
          } else {
            agregarAlCarrito(prod);
          }
          setSearchTerm('');
          return;
        }
      }
      // Fallback local
      const local = productos.find(p => String(p.codigo_barra_producto) === codigo);
      if (local) {
        const skipStock = codigo.length < 5;
        if (skipStock) {
          setQtyProduct(local);
          setQtyValue(1);
          setQtySkipStock(true);
          setShowQtyModal(true);
        } else {
          agregarAlCarrito(local);
        }
        setSearchTerm('');
        return;
      }
      
      Swal.fire({ icon: 'warning', title: 'No encontrado', text: 'El código de barras no coincide con ningún producto.', confirmButtonColor: '#FFC107' });
      
    } catch (error) {
      console.error('Error al buscar productos:', error);
    }
  };

  // Generador de ID único para items del carrito
  const generarCartId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const agregarAlCarrito = (producto: Producto, opts?: { skipStock?: boolean }) => {
    const skipStock = !!opts?.skipStock;
    const esKilo = (producto.id_unidad_medida ?? 0) === 2;
    console.log('agregarAlCarrito:', producto.nombre_producto, 'id_unidad_medida:', producto.id_unidad_medida, 'esKilo:', esKilo);
    if (verificarStock && !skipStock && !esKilo && producto.inventario <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin stock',
        text: 'Este producto no tiene stock disponible',
        confirmButtonColor: '#FFC107'
      });
      return;
    }

    const itemExistente = carrito.find(item => item.id_producto === producto.id_producto);

    //let esKilo2 = esKilo;
    let esKilo2 = 0;
    esKilo2 = 1; //forzamos que siempre sea kilo para que agregue como producto nuevo
    // Para productos por kilo (variable), NO mezclar - siempre agregar como item nuevo separado
    if (esKilo2) {
      const nuevoItem: SaleItem = {
        cartId: generarCartId(),
        id_producto: producto.id_producto,
        cantidad_producto: 1,
        precio_producto: producto.precio_venta_producto,
        descuento_producto: 0,
        nombre_producto: producto.nombre_producto
      };
      setCarrito(prev => [nuevoItem, ...prev]);
      return;
    }

    if (itemExistente) {
      const nuevaCantidad = itemExistente.cantidad_producto + 1;
      if (verificarStock && !skipStock && !esKilo && nuevaCantidad > producto.inventario) {
        Swal.fire({
          icon: 'warning',
          title: 'Stock insuficiente',
          text: `Solo hay ${producto.inventario} unidades disponibles`,
          confirmButtonColor: '#FFC107'
        });
        return;
      }
      setCarrito(prev => prev.map(item =>
        item.id_producto === producto.id_producto
          ? { ...item, cantidad_producto: nuevaCantidad }
          : item
      ));
    } else {
      const nuevoItem: SaleItem = {
        cartId: generarCartId(),
        id_producto: producto.id_producto,
        cantidad_producto: 1,
        precio_producto: producto.precio_venta_producto,
        descuento_producto: 0,
        nombre_producto: producto.nombre_producto
      };
      setCarrito(prev => [nuevoItem, ...prev]);
    }
  };

  const agregarCantidad = (producto: Producto, cantidad: number, opts?: { skipStock?: boolean }) => {
    const skipStock = !!opts?.skipStock;
    const esKilo = (producto.id_unidad_medida ?? 0) === 2;
    if (cantidad <= 0) return;
    if (verificarStock && !skipStock && !esKilo && cantidad > producto.inventario) {
      Swal.fire({ icon: 'warning', title: 'Stock insuficiente', text: `Solo hay ${producto.inventario} unidades disponibles`, confirmButtonColor: '#FFC107' });
      return;
    }
    setCarrito(prev => {
      const existing = prev.find(i => i.id_producto === producto.id_producto);
      if (existing) {
        const nuevaCantidad = existing.cantidad_producto + cantidad;
        if (verificarStock && !skipStock && !esKilo && nuevaCantidad > producto.inventario) {
          Swal.fire({ icon: 'warning', title: 'Stock insuficiente', text: `Solo hay ${producto.inventario} unidades disponibles`, confirmButtonColor: '#FFC107' });
          return prev;
        }
        return prev.map(i => i.id_producto === producto.id_producto ? { ...i, cantidad_producto: nuevaCantidad } : i);
      }
      const item: SaleItem = { cartId: generarCartId(), id_producto: producto.id_producto, cantidad_producto: cantidad, precio_producto: producto.precio_venta_producto, descuento_producto: 0, nombre_producto: producto.nombre_producto };
      return [...prev, item];
    });
  };

  const agregarProductoPorPeso = (producto: Producto) => {
    setWeightProduct(producto);
    setWeightGrams(1000);
    setShowWeightModal(true);
  };

  const confirmarAgregarPorPeso = () => {
    if (!weightProduct) return;
    const kilos = Math.max(0, weightGrams) / 1000;
    if (kilos <= 0) {
      Swal.fire({ icon: 'warning', title: 'Peso inválido', text: 'Ingresa un peso mayor a 0 gramos', confirmButtonColor: '#FFC107' });
      return;
    }

    // Productos por kilo SIEMPRE se agregan como item nuevo separado (nunca mezclar)
    const item: SaleItem = {
      cartId: generarCartId(),
      id_producto: weightProduct.id_producto,
      cantidad_producto: kilos,
      precio_producto: weightProduct.precio_venta_producto,
      descuento_producto: 0,
      nombre_producto: weightProduct.nombre_producto
    };
    setCarrito(prev => [item, ...prev]);
    setShowWeightModal(false);
    setWeightProduct(null);
    setWeightGrams(0);
  };

  const confirmarAgregarCantidad = () => {
    if (!qtyProduct) return;
    const unidades = Math.max(0, qtyValue);
    if (unidades <= 0) {
      Swal.fire({ icon: 'warning', title: 'Cantidad inválida', text: 'Ingresa una cantidad mayor a 0', confirmButtonColor: '#FFC107' });
      return;
    }
    agregarCantidad(qtyProduct, unidades, { skipStock: qtySkipStock });
    setShowQtyModal(false);
    setQtyProduct(null);
    setQtyValue(1);
    setQtySkipStock(false);

    setTimeout(() => {
      pagoRecibidoInputRef.current?.focus();
    }, 100);
    // pagoRecibidoInputRef.current?.focus();
    // });
  };

  const actualizarCantidad = (cartId: string, nuevaCantidad: number) => {
    // Buscar el item en el carrito por cartId
    const itemCarrito = carrito.find(i => i.cartId === cartId);
    if (!itemCarrito) return;

    // Buscar el producto en la lista local
    const productoLocal = productos.find(p => p.id_producto === itemCarrito.id_producto);
    const esKilo = (productoLocal?.id_unidad_medida ?? 0) === 2;

    // No permitir cantidad menor a 1
    if (nuevaCantidad < 1) {
      return;
    }

    if (verificarStock && !esKilo && productoLocal && nuevaCantidad > productoLocal.inventario) {
      Swal.fire({
        icon: 'warning',
        title: 'Stock insuficiente',
        text: `Solo hay ${productoLocal.inventario} unidades disponibles`,
        confirmButtonColor: '#FFC107'
      });
      return;
    }

    setCarrito(prev => prev.map(item =>
      item.cartId === cartId ? { ...item, cantidad_producto: nuevaCantidad }
        : item
    ));
  };

  const eliminarDelCarrito = (cartId: string) => {
    setCarrito(prev => prev.filter(item => item.cartId !== cartId));
  };

  const solicitarSupervisorYEliminar = async (cartId: string) => {
    const result = await Swal.fire({
      title: 'Eliminar producto',
      html: '<div class="text-start small text-muted mb-2">Se requiere clave de supervisor para eliminar del carrito.</div>',
      input: 'password',
      inputPlaceholder: 'Clave de supervisor',
      inputAttributes: { autocapitalize: 'off', autocorrect: 'off' },
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28A745',
      cancelButtonColor: '#6C757D',
      preConfirm: async (value) => {
        if (!value || String(value).trim() === '') {
          Swal.showValidationMessage('Ingrese la clave');
          return false;
        }
        try {
          const resp = await apiService.verifySupervisor(String(value));
          if (resp.estado === 1) return true;
          Swal.showValidationMessage('Clave inválida');
          return false;
        } catch (e) {
          Swal.showValidationMessage('Error verificando clave');
          return false;
        }
      }
    });
    if (result.isConfirmed) {
      eliminarDelCarrito(cartId);
      Swal.fire({ icon: 'success', title: 'Eliminado', timer: 1200, showConfirmButton: false });
    }
  };

  const limpiarCarritoConSupervisor = async () => {
    if (carrito.length === 0) return;
    const result = await Swal.fire({
      title: 'Limpiar carrito',
      html: '<div class="text-start small text-muted mb-2">Se requiere clave de supervisor para limpiar todo el carrito.</div>',
      input: 'password',
      inputPlaceholder: 'Clave de supervisor',
      inputAttributes: { autocapitalize: 'off', autocorrect: 'off' },
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#DC3545',
      cancelButtonColor: '#6C757D',
      preConfirm: async (value) => {
        if (!value || String(value).trim() === '') {
          Swal.showValidationMessage('Ingrese la clave');
          return false;
        }
        try {
          const resp = await apiService.verifySupervisor(String(value));
          if (resp.estado === 1) return true;
          Swal.showValidationMessage('Clave inválida');
          return false;
        } catch (e) {
          Swal.showValidationMessage('Error verificando clave');
          return false;
        }
      }
    });
    if (result.isConfirmed) {
      setCarrito([]);
      Swal.fire({ icon: 'success', title: 'Carrito limpiado', timer: 1200, showConfirmButton: false });
    }
  };

  const calcularTotal = () => {
    return carrito.reduce((total, item) => {
      const subtotal = item.cantidad_producto * item.precio_producto;
      const descuento = (subtotal * (item.descuento_producto || 0)) / 100;
      return total + (subtotal - descuento);
    }, 0);
  };

  const calcularVuelto = () => {
    const total = calcularTotal();
    return Math.max(0, pagoRecibido - total);
  };

  const procesarVenta = async () => {
    // Prevenir doble procesamiento
    if (processingVenta || loading) {
      return;
    }

    if (carrito.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Carrito vacío',
        text: 'Debe agregar productos al carrito',
        confirmButtonColor: '#FFC107'
      });
      return;
    }

    if (pagoRecibido < calcularTotal()) {
      Swal.fire({
        icon: 'warning',
        title: 'Pago insuficiente',
        text: 'El pago recibido es menor al total',
        confirmButtonColor: '#FFC107'
      });
      return;
    }

    setProcessingVenta(true);
    setLoading(true);
    
    try {
      const cajaConfig = (window as any).__APP_CONFIG__?.ID_CAJA;
      const ventaData: SaleData = {
        id_usuario: user.id_usuario,
        id_caja: (typeof cajaConfig === 'number' && cajaConfig > 0) ? cajaConfig : 1,
        id_medio_pago: medioPago,
        nombre_cliente_boleta: clienteNombre || 'Cliente',
        total_boleta: calcularTotal(),
        pago_boleta: pagoRecibido,
        vuelto_boleta: calcularVuelto(),
        descuento_boleta: 0,
        productos: carrito
      };

      const response = await apiService.createBoleta(ventaData);

      // Cerramos modal de confirmación antes de los sweet alerts para evitar conflictos
      setShowCheckout(false);

      if (response.estado === 1 && response.boleta) {
        // Imprimir ticket
        const receiptData = {
          boletaId: String(response.boleta.id_boleta),
          fecha: new Date().toLocaleString('es-CL'),
          productos: carrito.map(item => ({
            nombre: item.nombre_producto || 'Producto',
            cantidad: item.cantidad_producto,
            precio: item.precio_producto
          })),
          total: calcularTotal(),
          pago: pagoRecibido,
          vuelto: calcularVuelto()
        };

        // Omite impresión de ticket en web; solo genera/descarga PDF DTE si está disponible

        // Intentar imprimir PDF DTE si está disponible
        try {
          const dte = (response as any)?.dte;
          if (dte) {
            const saveRes = await printService.saveDtePdfToApp(dte);
            if (saveRes?.path) {
              // Imprimir automáticamente con Sumatra usando configuración de impresora
              const pr = await printService.printPdf(saveRes.path);
              if (!pr.success) console.warn('Error imprimiendo PDF DTE:', pr.message);
            } else if (!saveRes.success) {
              console.warn('No se pudo guardar PDF DTE:', saveRes.message);
            }
          }
        } catch (e) {
          console.warn('Error imprimiendo DTE PDF:', e);
        }

        Swal.fire({
          icon: 'success',
          title: '¡Venta realizada!',
          text: `Boleta #${response.boleta.id_boleta} generada exitosamente`,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#28A745',
          timer: 500
        });

        // Guardar vuelto anterior antes de limpiar
        const vueltoActual = calcularVuelto();
        setVueltoAnterior(vueltoActual);
        try { localStorage.setItem('pos_vuelto_anterior', String(vueltoActual)); } catch {}

        // Limpiar carrito
        setCarrito([]);
        setClienteNombre('');
        setPagoRecibido(0);
        
        // Recargar productos para actualizar stock
        cargarProductos();      
        
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response.msg || 'No se pudo procesar la venta',
          confirmButtonColor: '#DC3545'
        });
      }
    } catch (error) {
      console.error('Error al procesar venta:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al procesar la venta',
        confirmButtonColor: '#DC3545'
      });
    } finally {
      setLoading(false);
      setProcessingVenta(false);

       // Focus on the product search input after transaction completes
       setTimeout(() => {
        searchInputRef.current?.focus();
       }, 100);
    }
  };

  useEffect(() => {

    const handler = (e: KeyboardEvent) => {
      // No capturar atajos globales si hay otros modales abiertos (evita conflicto con el Enter)
      if (document.querySelector('.swal2-container') || showQtyModal || showWeightModal || showPanaModal) {
        return;
      }

      const active = document.activeElement as HTMLElement | null;
      const isSearchFocused = !!active && active === searchInputRef.current;
      if (e.key === 'F1') {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'F2') {
        e.preventDefault();
        panaBtnRef.current?.click();
        setTimeout(() => {
          const panaInput = document.querySelector('input[placeholder="Código de barra o ID Despacho"]') as HTMLInputElement;
          if (panaInput) panaInput.focus();
        }, 100);
      } else if (e.key === 'F3') {
        e.preventDefault();
        medioPagoRef.current?.focus();
      } else if (e.key === 'Enter') {
        //if(estadoEnter===false) return;
        if (isSearchFocused && !processingVenta) {
          // Permitir submit de formulario de búsqueda con Enter
          return;
        }
        if (showCheckout && !processingVenta) {
          e.preventDefault();
          procesarVenta();
        } else if (!showCheckout && carrito.length > 0) {
          e.preventDefault();
          if (pagoRecibido < calcularTotal()) setPagoRecibido(calcularTotal());
          setShowCheckout(true);
        }
      } else if (e.key === 'F4') {
        e.preventDefault();
        pagoRecibidoInputRef.current?.focus();
      } else if (e.key === 'F5') {
        e.preventDefault();
        if (!showCheckout && carrito.length > 0) {
          if (pagoRecibido < calcularTotal()) setPagoRecibido(calcularTotal());
          setShowCheckout(true);
          setTimeout(() => {
            pagoRecibidoInputRef.current?.focus();
          }, 100);
        } else if (showCheckout && !processingVenta) {
          procesarVenta();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showCheckout, carrito, showQtyModal, showWeightModal, showPanaModal, pagoRecibido, processingVenta]);

  return (
    <Container fluid>
      <Row>
        {/* Panel de Productos */}
        <Col lg={8}>
          <Card className="shadow-sm border-0 mb-3" style={{ position: 'relative' }}>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-search me-2"></i>
                Buscar Productos <Badge bg="light" text="dark" className="ms-2">F1</Badge>
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={buscarProducto}>
                <Row>
                  <Col md={8} style={{ position: 'relative' }}>
                    <Form.Control
                      type="text"
                      placeholder="Buscar por nombre o código de barras..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={handleKeyDownAutocomplete}
                      className="mb-2"
                      ref={searchInputRef}
                    />
                    {showAutocomplete && autocompleteResults.length > 0 && (
                      <div className="autocomplete-dropdown" style={{
                        position: 'absolute', top: '100%', left: '12px', right: '12px',
                        background: '#fff', border: '1px solid #ccc', zIndex: 1000,
                        maxHeight: '300px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                        {autocompleteResults.map((prod, idx) => (
                          <div
                            key={prod.id_producto}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                              backgroundColor: focusedIndex === idx ? '#e9ecef' : '#fff'
                            }}
                            onMouseEnter={() => setFocusedIndex(idx)}
                            onClick={() => seleccionarProductoAutocomplete(prod)}
                          >
                            <div className="d-flex justify-content-between">
                              <span className="fw-bold">{prod.nombre_producto}</span>
                              <span className="text-success fw-bold">${prod.precio_venta_producto.toLocaleString('es-CL')}</span>
                            </div>
                            <div className="d-flex justify-content-between small text-muted">
                              <span>Cod: {prod.codigo_barra_producto}</span>
                              {verificarStock && <span>Stock: {(prod.id_unidad_medida ?? 0) === 2 ? '∞' : prod.inventario}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Col>
                  <Col md={4}>
                    <Button type="submit" variant="primary" className="w-100 mb-2">
                      <i className="fas fa-search me-2"></i>
                      Buscar
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0">
            <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-shopping-cart me-2"></i>
                Carrito de Compras ({carrito.length})
              </h5>
              {carrito.length > 0 && (
                <Button
                  size="sm"
                  variant="outline-light"
                  onClick={limpiarCarritoConSupervisor}
                  title="Limpiar todo el carrito"
                >
                  <i className="fas fa-trash-alt me-1"></i>
                  Limpiar
                </Button>
              )}
            </Card.Header>
            <Card.Body style={{ minHeight: '400px', maxHeight: '600px', overflowY: 'auto' }} ref={carritoContainerRef} tabIndex={-1}>
              {carrito.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-shopping-cart fa-4x mb-3 text-light"></i>
                  <h4>El carrito está vacío</h4>
                  <p>Busca un producto y agrégalo para comenzar la venta</p>
                </div>
              ) : (
                <Table responsive hover className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Producto</th>
                      <th className="text-center" style={{ width: '150px' }}>Cant</th>
                      <th className="text-end">Precio</th>
                      <th className="text-end">Total</th>
                      <th className="text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carrito.map((item) => {
                      const subtotal = item.cantidad_producto * item.precio_producto;
                      return (
                        <tr key={item.cartId}>
                          <td className="fw-semibold text-truncate" style={{ maxWidth: 300 }}>{item.nombre_producto}</td>
                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center">
                              <Button size="sm" variant="outline-secondary" onClick={() => actualizarCantidad(item.cartId, item.cantidad_producto - 1)} className="px-2" disabled={item.cantidad_producto <= 1}>-</Button>
                              <Form.Control 
                                type="number" 
                                size="sm" 
                                className="mx-1 text-center" 
                                style={{ width: '60px' }} 
                                value={item.cantidad_producto}
                                onChange={(e) => actualizarCantidad(item.cartId, Number(e.target.value))}
                              />
                              <Button size="sm" variant="outline-secondary" onClick={() => actualizarCantidad(item.cartId, item.cantidad_producto + 1)} className="px-2">+</Button>
                            </div>
                          </td>
                          <td className="text-end">${item.precio_producto.toLocaleString('es-CL')}</td>
                          <td className="text-end fw-bold">${subtotal.toLocaleString('es-CL')}</td>
                          <td className="text-center">
                            <Button size="sm" variant="outline-danger" onClick={() => solicitarSupervisorYEliminar(item.cartId)}>
                              <i className="fas fa-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Panel Derecho (Checkout) */}
        <Col lg={4}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-dark text-white">
              <h5 className="mb-0"><i className="fas fa-cash-register me-2"></i>Resumen de Venta</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <Form.Label className="fw-semibold">Cliente</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Nombre del cliente (opcional)"
                  value={clienteNombre}
                  onChange={(e) => setClienteNombre(e.target.value)}
                />
              </div>
              
              <div className="mb-3">
                <Form.Label className="fw-semibold">Medio de Pago <Badge bg="secondary" className="ms-2">F3</Badge></Form.Label>
                <Form.Select ref={medioPagoRef} value={medioPago} onChange={(e) => setMedioPago(Number(e.target.value))}>
                  <option value={1}>Efectivo</option>
                  <option value={2}>Tarjeta Débito</option>
                  <option value={3}>Tarjeta Crédito</option>
                  <option value={4}>Transferencia</option>
                </Form.Select>
              </div>

              {medioPago === 1 && (
                <div className="mb-3">
                  <Form.Label className="fw-semibold">Pago Recibido <Badge bg="secondary" className="ms-2">F4</Badge></Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Monto recibido"
                    defaultValue={pagoRecibido || ''}
                    onChange={(e) => {                      
                      setPagoRecibido(Number(e.target.value) || 0);                      
                    }}
                    ref={pagoRecibidoInputRef}
                  />
                </div>
              )}

              <div className="border-top pt-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold text-muted">Subtotal:</span>
                  <span className="fw-semibold text-muted">
                    ${Math.round(calcularTotal() / 1.19).toLocaleString('es-CL')}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold text-muted">IVA (19%):</span>
                  <span className="fw-semibold text-muted">
                    ${Math.round(calcularTotal() - (calcularTotal() / 1.19)).toLocaleString('es-CL')}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                  <span className="fw-bold fs-5">Total Final:</span>
                  <span className="fw-bold text-success fs-4">
                    ${calcularTotal().toLocaleString('es-CL')}
                  </span>
                </div>
              </div>

              <Button
                variant="warning"
                className="w-100 fw-bold mb-2"
                onClick={() => { setShowPanaModal(true); setPanaCodigo(''); setPanaDespacho(null); }}
                ref={panaBtnRef}
              >
                <i className="fas fa-qrcode me-2"></i>
                Pagar voucher Pana <Badge bg="light" text="dark" className="ms-2">F2</Badge>
              </Button>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="fw-semibold">Vuelto:</span>
                <span className="fw-bold text-success fs-6">
                  ${calcularVuelto().toLocaleString('es-CL')}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="fw-semibold text-muted">Vuelto Boleta Anterior:</span>
                <span className="fw-bold text-warning fs-6">
                  ${vueltoAnterior.toLocaleString('es-CL')}
                </span>
              </div>

              <Button
                variant="success"
                className="w-100 fw-bold"
                onClick={() => {
                  if (pagoRecibido < calcularTotal()) setPagoRecibido(calcularTotal());
                  setShowCheckout(true);
                }}
                disabled={carrito.length === 0}
              >
                <i className="fas fa-cash-register me-2"></i>
                Procesar Venta <Badge bg="light" text="dark" className="ms-2">F5</Badge>
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de Confirmación */}
      <Modal show={showCheckout} onHide={() => setShowCheckout(false)} size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="fas fa-check-circle me-2"></i>
            Confirmar Venta
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            <h6 className="fw-bold mb-3">Resumen de la Venta:</h6>
            <Table responsive size="sm">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th className="text-center">Cantidad</th>
                  <th className="text-end">Precio Unit.</th>
                  <th className="text-end">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {carrito.map((item) => {
                  const subtotal = item.cantidad_producto * item.precio_producto;
                  return (
                    <tr key={item.id_producto}>
                      <td>{item.nombre_producto}</td>
                      <td className="text-center">{item.cantidad_producto}</td>
                      <td className="text-end">${item.precio_producto.toLocaleString('es-CL')}</td>
                      <td className="text-end">${subtotal.toLocaleString('es-CL')}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="fw-bold">
                  <td colSpan={3} className="text-end">TOTAL:</td>
                  <td className="text-end text-primary fs-5">
                    ${calcularTotal().toLocaleString('es-CL')}
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <strong>Cliente:</strong> {clienteNombre || 'Cliente'}
            </div>
            <div className="col-md-6">
              <strong>Medio de Pago:</strong> 
              {medioPago === 1 ? 'Efectivo' : 
               medioPago === 2 ? 'Tarjeta Débito' :
               medioPago === 3 ? 'Tarjeta Crédito' : 'Transferencia'}
            </div>
          </div>

          {medioPago === 1 && (
            <div className="row align-items-center bg-light p-3 rounded">
              <div className="col-md-6">
                <Form.Label className="fw-bold mb-1">Pago Recibido:</Form.Label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="number"
                    defaultValue={pagoRecibido || ''}
                    onChange={(e) => {
                      const val = Number(e.target.value) || 0;
                      setPagoRecibido(val);
                    }}
                    ref={pagoRecibidoInputRef}
                    autoFocus
                    className="fw-bold text-success fs-5"
                  />
                </div>
              </div>
              <div className="col-md-6 text-end">
                <span className="fw-bold d-block text-muted mb-1">Vuelto:</span>
                <span className="fw-bold text-primary fs-3">${calcularVuelto().toLocaleString('es-CL')}</span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCheckout(false)}>
            <i className="fas fa-times me-2"></i>
            Cancelar
          </Button>
          <Button 
            variant="success" 
            onClick={procesarVenta}
            disabled={loading || processingVenta}
            className="fw-bold"
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Procesando...
              </>
            ) : (
              <>
                <i className="fas fa-check me-2"></i>
                Confirmar Venta
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Pagar voucher (Pana) */}
      <Modal show={showPanaModal} onHide={() => { setShowPanaModal(false); setPanaDespacho(null); setPanaCodigo(''); }} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-qrcode me-2"></i>
            Pagar voucher – Pana
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e: any) => { e.preventDefault(); buscarDespacho(); }}>
            <div className="d-flex gap-2 mb-3">
              <Form.Control placeholder="Código de barra o ID Despacho" value={panaCodigo} onChange={(e: any) => setPanaCodigo(e.target.value)} />
              <Button variant="primary" onClick={buscarDespacho} disabled={panaLoading}>
                {panaLoading ? 'Buscando...' : 'Buscar'}
              </Button>
            </div>
          </Form>
          {panaDespacho && (
            <div className="border rounded p-3">
              <div className="mb-2"><strong>Cliente:</strong> {panaDespacho.cliente}</div>
              <div className="mb-2"><strong>Repartidor:</strong> {panaDespacho.repartidor}</div>
              <div className="mb-2"><strong>Estado pago:</strong> {panaDespacho.estado_pago === 1 ? 'Pagado' : 'Pendiente'}</div>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th className="text-end">Kilos</th>
                      <th className="text-end">Unidades</th>
                      <th className="text-end">Precio/Kg</th>
                      <th className="text-end">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {panaDespacho.detalle.map((d, idx) => (
                      <tr key={idx}>
                        <td>{d.nom_producto}</td>
                        <td className="text-end">{d.kilos ?? '-'}</td>
                        <td className="text-end">{d.unidades ?? '-'}</td>
                        <td className="text-end">{d.precio_kilo_producto ? `$${d.precio_kilo_producto.toLocaleString('es-CL')}` : '-'}</td>
                        <td className="text-end">${(d.subtotal || 0).toLocaleString('es-CL')}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colSpan={4} className="text-end">Total</th>
                      <th className="text-end">${panaDespacho.total.toLocaleString('es-CL')}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowPanaModal(false); setPanaDespacho(null); setPanaCodigo(''); }}>Cerrar</Button>
          <Button variant="success" onClick={marcarPagado} disabled={!panaDespacho || panaDespacho.estado_pago === 1 || panaLoading}>
            <i className="fas fa-check me-2"></i>
            Marcar pagado
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal Agregar por Peso */}
      <Modal show={showWeightModal} onHide={() => setShowWeightModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-scale-balanced me-2"></i>
            Agregar por peso
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <strong>Producto:</strong> {weightProduct?.nombre_producto}
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Peso (gramos)</Form.Label>
            <Form.Control type="number" min={1} step={1} value={weightGrams}
              onChange={(e: any) => setWeightGrams(parseInt(e.target.value || '0', 10))} />
            <div className="small text-muted mt-1">Equivale a {(Math.max(0, weightGrams) / 1000).toFixed(3)} kg</div>
          </Form.Group>
          <div className="d-flex gap-2 mb-3">
            {[100,250,500,1000].map(g => (
              <Button key={g} variant="outline-secondary" size="sm" onClick={() => setWeightGrams(g)}>{g} g</Button>
            ))}
          </div>
          <div className="mt-2">
            <strong>Precio por kg:</strong> ${ (weightProduct?.precio_venta_producto ?? 0).toLocaleString('es-CL') }
          </div>
          <div>
            <strong>Subtotal:</strong> ${ ((Math.max(0, weightGrams)/1000) * (weightProduct?.precio_venta_producto ?? 0)).toLocaleString('es-CL') }
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWeightModal(false)}>Cancelar</Button>
          <Button variant="success" onClick={confirmarAgregarPorPeso}>
            <i className="fas fa-cart-plus me-2"></i>
            Agregar al carrito
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Ingresar Cantidad (códigos cortos) */}
      <Modal show={showQtyModal} onHide={() => setShowQtyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-hashtag me-2"></i>
            Ingresar cantidad
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2"><strong>Producto:</strong> {qtyProduct?.nombre_producto}</div>
          <Form.Group className="mb-3">
            <Form.Label>Cantidad (unidades)</Form.Label>
            <Form.Control
              ref={qtyInputRef}
              type="number"
              min={1}
              step={1}
              value={qtyValue}
              onChange={(e: any) => setQtyValue(parseFloat(e.target.value || '0'))}
              onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); confirmarAgregarCantidad(); } }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQtyModal(false)}>Cancelar</Button>
          <Button variant="success" onClick={() => confirmarAgregarCantidad()}>
            <i className="fas fa-cart-plus me-2"></i>
            Agregar al carrito
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PuntoVentaV2;
